import { CumulativeBooster } from "../cumulative_booster.js";
import { CumulativeBoosterID } from "../cumulative_booster_id.js";
import type { FireworksTarget } from "./fireworks_booster.js";

export interface MineHandle
{
  isFinished(): boolean;
}

export interface MineBoosterContext
{
  /** Targets whose camera-facing side is not covered by another block. */
  getRandomFrontTargets( count: number ): FireworksTarget[];
  launchMineAt( target: FireworksTarget, fuse_seconds: number ): MineHandle;
}

/**
 * Level-start passive booster: launches mines that stick to blocks, blink
 * while their fuse burns and then blow up the surrounding area. Completes
 * once every launched mine has exploded.
 */
export class MineBooster extends CumulativeBooster<MineBoosterContext>
{
  public static readonly DEFAULT_MINE_COUNT = 3;
  public static readonly DEFAULT_LAUNCH_INTERVAL_SECONDS = 0.2;
  public static readonly DEFAULT_FUSE_SECONDS = 1.6;

  private readonly mine_count: number;
  private readonly launch_interval_seconds: number;
  private readonly fuse_seconds: number;
  private targets: FireworksTarget[] = [];
  private mines: MineHandle[] = [];
  private time_to_next_launch = 0;

  public constructor(
    mine_count = MineBooster.DEFAULT_MINE_COUNT,
    launch_interval_seconds = MineBooster.DEFAULT_LAUNCH_INTERVAL_SECONDS,
    fuse_seconds = MineBooster.DEFAULT_FUSE_SECONDS
  )
  {
    super( CumulativeBoosterID.Mine );
    this.mine_count = Math.max( 1, Math.floor( mine_count ) );
    this.launch_interval_seconds = Math.max( 0, launch_interval_seconds );
    this.fuse_seconds = Math.max( 0, fuse_seconds );
  }

  protected override onActivate( context: MineBoosterContext ): void
  {
    this.targets = context.getRandomFrontTargets( this.mine_count );
    this.mines = [];
    this.time_to_next_launch = 0;
    if ( this.targets.length === 0 )
      this.complete();
    else
      this.launchNext( context );
  }

  protected override onUpdate( context: MineBoosterContext, dt: number ): void
  {
    this.time_to_next_launch -= dt;
    while ( this.mines.length < this.targets.length && this.time_to_next_launch <= 0 )
      this.launchNext( context );

    if ( this.mines.length >= this.targets.length && this.mines.every( mine => mine.isFinished() ) )
      this.complete();
  }

  protected override onReset(): void
  {
    this.targets = [];
    this.mines = [];
    this.time_to_next_launch = 0;
  }

  private launchNext( context: MineBoosterContext ): void
  {
    this.mines.push( context.launchMineAt( this.targets[this.mines.length], this.fuse_seconds ) );
    this.time_to_next_launch += this.launch_interval_seconds;
  }
}
